import React, { useState, useCallback } from 'react';
import { Search, Save } from 'lucide-react';
import NaturalLanguageInput from './NaturalLanguageInput';
import QueryPlanPreview from './QueryPlanPreview';
import ResultsTable from './ResultsTable';
import RefinementPanel from './RefinementPanel';
import SessionHistory from './SessionHistory';

export default function FederatedSearchPlanner({ role }) {
  const [phase, setPhase] = useState('input'); // 'input'|'plan'|'results'
  const [planHistory, setPlanHistory] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [articles, setArticles] = useState([]);
  const [signals, setSignals] = useState(new Map());
  const [currentRunId, setCurrentRunId] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [refineLoading, setRefineLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const currentPlan = planHistory[currentIdx]?.plan || null;

  async function handleBuild(description) {
    setAiLoading(true); setError(null);
    try {
      const r = await window.zignagenixAPI.aiBuildQuery({ description, enabled_sources: null });
      if (r.error) { setError(r.message); return; }
      const entry = { plan: r.plan, runId: r.run_id, timestamp: Date.now() };
      setPlanHistory([entry]);
      setCurrentIdx(0);
      setSignals(new Map());
      setArticles([]);
      setPhase('plan');
    } catch (e) { setError(e.message); }
    finally { setAiLoading(false); }
  }

  async function handleSearch(plan, querySource) {
    setSearchLoading(true); setError(null); setSignals(new Map());
    try {
      const r = await window.zignagenixAPI.executeAdhocSearch({
        plan, run_id: currentRunId, query_source: querySource || 'ai_generated',
      });
      if (r.error) { setError(r.message); return; }
      setCurrentRunId(r.run_id);
      setArticles(r.articles || []);
      setPhase('results');
    } catch (e) { setError(e.message); }
    finally { setSearchLoading(false); }
  }

  const handleSignal = useCallback((articleId, signal) => {
    setSignals(prev => {
      const next = new Map(prev);
      if (next.get(articleId) === signal) next.delete(articleId); // toggle off
      else next.set(articleId, signal);
      return next;
    });
  }, []);

  async function handleRefine() {
    if (!currentRunId || signals.size < 5) return;
    setRefineLoading(true); setError(null);
    const signalPayload = [...signals.entries()].map(([id, signal]) => {
      const art = articles.find(a => String(a.pmid || a.doi) === id) || {};
      return { article_id: id, title: art.title || id, abstract: (art.abstract || '').slice(0, 500), signal };
    });
    try {
      const r = await window.zignagenixAPI.aiRefineQuery({
        previous_run_id: currentRunId, relevance_signals: signalPayload,
      });
      if (r.error) { setError(r.message); return; }
      const entry = { plan: r.plan, runId: r.run_id, timestamp: Date.now() };
      const newHistory = [...planHistory.slice(0, currentIdx + 1), entry];
      setPlanHistory(newHistory);
      setCurrentIdx(newHistory.length - 1);
      setSignals(new Map()); setArticles([]);
      setPhase('plan');
    } catch (e) { setError(e.message); }
    finally { setRefineLoading(false); }
  }

  function handleRollback(idx) {
    setCurrentIdx(idx);
    setSignals(new Map()); setArticles([]);
    setPhase('plan');
  }

  async function handleSave() {
    if (!currentPlan) return;
    const s = currentPlan.structured || {};
    try {
      const r = await window.zignagenixAPI.createSearchConfig({
        name: `AI Search — ${new Date().toLocaleDateString()}`,
        search_terms: s.search_terms || '',
        mesh_terms: s.mesh_terms || '',
        condition_filter: s.condition_filter || '',
        study_type_filter: s.study_type_filter || '',
        date_range_months: s.date_range_months || 12,
        frequency: 'weekly',
      });
      if (!r.error) setSaveSuccess(true);
      else setError(r.message);
    } catch (e) { setError(e.message); }
  }

  return (
    <div className="fs-planner">
      <div className="fs-planner-header">
        <Search size={16} color="#2563EB" />
        <h3 className="fs-planner-title">Federated Literature Search</h3>
        <span className="fs-planner-badge">AI-Assisted · Opus 4.7</span>
      </div>

      {error && (
        <div className="fs-error" role="alert">
          {error}
          <button className="fs-error-dismiss" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="fs-planner-body">
        <div className="fs-planner-main">
          {(phase === 'input' || phase === 'plan' || phase === 'results') && (
            <NaturalLanguageInput
              onBuild={handleBuild}
              loading={aiLoading}
              disabled={searchLoading || refineLoading}
            />
          )}

          {(phase === 'plan' || phase === 'results') && currentPlan && (
            <QueryPlanPreview
              plan={currentPlan}
              onPlanChange={() => {}}
              onSearch={handleSearch}
              loading={searchLoading}
            />
          )}

          {phase === 'results' && (
            <>
              <div className="fs-results-header">
                <span className="fs-results-count">{articles.length} articles returned</span>
                {currentPlan && (
                  <button className="fs-btn fs-btn--ghost" onClick={handleSave} title="Save as scheduled search config">
                    <Save size={13} /> Save as Search Config
                  </button>
                )}
                {saveSuccess && <span className="fs-save-ok">Saved ✓</span>}
              </div>
              <ResultsTable articles={articles} signals={signals} onSignal={handleSignal} />
              <RefinementPanel signals={signals} onRefine={handleRefine} loading={refineLoading} />
            </>
          )}
        </div>

        <SessionHistory
          history={planHistory}
          currentIndex={currentIdx}
          onRollback={handleRollback}
        />
      </div>
    </div>
  );
}
